import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/apiClient";
import { startGoogleLogin } from "../lib/googleAuth";
import { attachFunnel } from "../lib/analytics";
import { toast } from "sonner";
import { FooterLinks } from "../components/Footer";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginGoogle = () => { startGoogleLogin(); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      await attachFunnel();
      toast.success("Bienvenue !");
      navigate(user.onboarded ? "/dashboard" : "/onboarding");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "EMAIL_NOT_VERIFIED") {
        toast.info("Vérifie ton compte pour continuer.");
        navigate("/verify-email", { state: { email } });
        return;
      }
      setError(formatApiErrorDetail(detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-amber-600/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-amber-800/10 blur-3xl" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="flex items-center gap-2 justify-center mb-8">
          <img src={process.env.PUBLIC_URL + "/cehnura-mark.png"} alt="Cehnura" className="w-11 h-11 object-contain" />
          <span className="font-display font-extrabold text-2xl">Cehn<span className="gold-text">ura</span></span>
        </div>
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-8 relative grain">
          <div className="relative z-10">
            <h1 className="font-display text-2xl font-bold mb-1">Content de te revoir</h1>
            <p className="text-sm text-zinc-500 mb-6">Reprends ta progression.</p>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Email" type="email" value={email} onChange={setEmail} testid="login-email" placeholder="ton@email.com" />
              <Field label="Mot de passe" type="password" value={password} onChange={setPassword} testid="login-password" placeholder="••••••••" />
              <div className="text-right -mt-2">
                <Link to="/forgot-password" className="text-xs text-amber-400 hover:underline" data-testid="forgot-password-link">Mot de passe oublié ?</Link>
              </div>
              {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Se connecter"}
              </button>
            </form>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-zinc-800" /><span className="text-xs text-zinc-600">ou</span><div className="flex-1 h-px bg-zinc-800" />
            </div>
            <button type="button" onClick={loginGoogle} data-testid="google-login-btn"
              className="w-full h-12 rounded-xl bg-white text-zinc-900 font-semibold flex items-center justify-center gap-2 hover:bg-zinc-100 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              Continuer avec Google
            </button>
            <p className="text-sm text-zinc-500 mt-6 text-center">
              Pas encore de compte ?{" "}
              <Link to="/register" className="text-amber-400 hover:underline" data-testid="go-register">Inscris-toi</Link>
            </p>
          </div>
        </div>
        <FooterLinks />
      </motion.div>
    </div>
  );
}

function Field({ label, type, value, onChange, testid, placeholder }) {
  const [show, setShow] = useState(false);
  const isPwd = type === "password";
  const inputType = isPwd ? (show ? "text" : "password") : type;
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          placeholder={placeholder}
          required
          className={`w-full h-12 px-4 ${isPwd ? "pr-11" : ""} rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none transition-colors`}
        />
        {isPwd && (
          <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${testid}-toggle`} aria-label={show ? "Masquer" : "Afficher"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-amber-400">
            {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}
      </div>
    </div>
  );
}
